import { useRecoilValue, useSetRecoilState } from "recoil";
import { datadogRum } from "@datadog/browser-rum";
import {
  billPreviewsState,
  billPreviewsLoadingState,
} from "../../recoil/atoms";
import { getUserAuth } from "../../recoil/selectors";
import {
  GetBillPreviews,
  Bill,
  BillPreview,
} from "../../components/UpfrontPayment/Services/UpfrontPaymentService";

/**
 * Core function to fetch and update bills.
 */
export const fetchAndUpdateBillPreviews = async (
  accessToken: string,
  setBillPreviews: React.Dispatch<React.SetStateAction<BillPreview[]>>,
  setBillPreviewsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  signal?: AbortSignal,
): Promise<void> => {
  setBillPreviewsLoading(true);
  try {
    const response = await GetBillPreviews(accessToken, signal);
    if (response.ok) {
      const bills = await response.json();
      setBillPreviews(bills);
    }
  } catch (error) {
    // Don't log errors for aborted requests
    if (error instanceof Error && error.name === "AbortError") {
      return;
    }
    // Log to Datadog in production/staging environments
    if (
      import.meta.env.VITE_ENV === "production" ||
      import.meta.env.VITE_ENV === "staging"
    ) {
      datadogRum.addError(error as Error, {
        source: "bills_load_failed",
        message: "Failed to load patient bills",
      });
    } else {
      console.error("Failed to load patient bills:", error);
    }
  } finally {
    setBillPreviewsLoading(false);
  }
};

/**
 * Hook to refresh bills data after payment operations.
 * Call this after successful payments to ensure bills are up-to-date.
 *
 * @example
 * const refreshBillPreviews = useBillPreviewsRefresh();
 * // After successful payment:
 * await makePayment();
 * refreshBillPreviews();
 */
export const useBillPreviewsRefresh = () => {
  const auth = useRecoilValue(getUserAuth);
  const setBillPreviews = useSetRecoilState(billPreviewsState);
  const setBillPreviewsLoading = useSetRecoilState(billPreviewsLoadingState);

  return async () => {
    if (auth?.access_token) {
      await fetchAndUpdateBillPreviews(
        auth.access_token,
        setBillPreviews,
        setBillPreviewsLoading,
      );
    }
  };
};
